import { FC, useEffect, useState, useCallback } from 'react';
import { useAppSelector } from '../../redux/hooks';
import Loader from '../loader/Loader';
import { SelectedSpecimenQuantity } from '../specimen-tables/SelectSpecimens';
import { Animal } from '../../types/interfaces';
import { Specimen, SpecimenInventory } from '../../types/interfaces/specimen.interfaces';
import { getAccountAnimals } from '../../api/animalsApi';
import { getSpecimensByOwnership } from '../../api/specimensApi';
import { showToast } from '../../services/toast.service';
import { toastMessages } from '../../constants/errorMessages';
import { LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../constants/common';
import './multiAnimalSpecimenSelector.scss';
import { TransactionStatusEnum } from '../../types/enums';

type Props = {
  accountId: number;
  onChange: (e: { valid: boolean; value: SelectedSpecimenQuantity[] }) => void;
};

const MultiAnimalSpecimenSelector: FC<Props> = ({ accountId, onChange }) => {
  const user = useAppSelector(state => state.user);

  const [animals, setAnimals] = useState<{ animal: Animal; quantityAvailable: number }[]>([]);
  const [selectedAnimalIds, setSelectedAnimalIds] = useState<number[]>([]);
  const [specimens, setSpecimens] = useState<Specimen[]>([]);
  const [quantities, setQuantities] = useState<SelectedSpecimenQuantity[]>([]);
  const [loadingAnimal, setLoadingAnimals] = useState(false);
  const [loadingSpecimens, setLoadingSpecimens] = useState(false);

  const fetchAnimals = useCallback(async () => {
    setLoadingAnimals(true);
    try {
      const { data } = await getAccountAnimals(accountId, { include: 'Breed.Species, Specimens, Owners' });
      const animalsFetched = data as Animal[];

      const animalQuantitiesWithTotals = await Promise.all(
        animalsFetched.map(async animal => {
          try {
            const params = {
              excludeStatuses: [TransactionStatusEnum.Pending, TransactionStatusEnum.Hold, TransactionStatusEnum.Processed],
            };
            const specs = await getSpecimensByOwnership(animal.animalId!, accountId, params);
            const quantityAvailable = specs.data.reduce((sum: number, s: SpecimenInventory) => sum + s.quantityAvailable, 0);
            return { animal, quantityAvailable };
          } catch {
            return { animal, quantityAvailable: 0 };
          }
        }),
      );
      setAnimals(animalQuantitiesWithTotals);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setLoadingAnimals(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (accountId) {
      fetchAnimals();
    }
  }, [fetchAnimals]);

  const toggleAnimal = async (animalId: number, checked: boolean) => {
    setSelectedAnimalIds(prev => {
      return checked ? [...prev, animalId] : prev.filter(id => id !== animalId);
    });

    if (checked) {
      if (specimens.some(s => s.animal?.animalId === animalId)) {
        return;
      }

      setLoadingSpecimens(true);
      try {
        const params = {
          include: 'Animal, SpecimenType, SpecimenLocation,SpecimenCustomDataValue, EmbryoStraw',
          userId: user.userId,
          getUpdatedAvailableQuantities: true,
          excludeStatuses: [TransactionStatusEnum.Pending, TransactionStatusEnum.Hold, TransactionStatusEnum.Processed],
        };
        const result = await getSpecimensByOwnership(animalId, accountId, params);
        const newSpecs = result.data as SpecimenInventory[];

        const flattenedSpecs: SpecimenInventory[] = newSpecs.flatMap(spec => {
          const animalGender = spec.animal?.gender?.toLowerCase();
          const flushMating =
            spec.specimenCustomDataValues?.find(item => item.specimenCustomDataKey?.keyName === 'Flush Mating')?.value ?? '-';

          if (animalGender !== 'female' || !spec.embryoStraws?.length) {
            return [spec];
          }

          return spec.embryoStraws.map(straw => ({
            ...spec,
            embryoStraws: [straw],
            embryoStrawId: straw.embryoStrawId,
            embryoStrawCode: straw.embryoStrawCode,
            quantityAvailable: straw.embryoCountPerStraw ?? 1,
            flushMating,
          }));
        });

        const newQuantities = flattenedSpecs.map(spec => {
          const isEmbryo = spec.specimenType?.name?.toLowerCase() === 'embryo';
          const availableQty = spec.quantityAvailable;

          return {
            specimenId: spec.specimenId,
            availableQuantity: availableQty,
            embryoStrawId: spec.embryoStrawId,
            selectedCanisterId: spec.specimenLocations?.[0]?.storageCanisterId,
            specimenLocationId: spec.specimenLocations?.[0]?.specimenLocationId,
            quantity: isEmbryo ? availableQty : 0,
            selected: false,
            isEmbryo,
          };
        });

        setSpecimens(prev => [...prev, ...flattenedSpecs]);
        setQuantities(prev => [...prev, ...newQuantities]);
      } catch (err) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setLoadingSpecimens(false);
      }
    } else {
      setSpecimens(prevSpecs => {
        const removedSpecimenIds = prevSpecs
          .filter(specimen => specimen.animal?.animalId === animalId)
          .map(specimen => specimen.specimenId);
        setQuantities(prevQuants => prevQuants.filter(quantityItem => !removedSpecimenIds.includes(quantityItem.specimenId)));
        return prevSpecs.filter(specimen => specimen.animal?.animalId !== animalId);
      });
    }
  };

  const handleCheckboxChange = (index: number, checked: boolean) => {
    const newQuantities = [...quantities];
    const item = newQuantities[index];
    newQuantities[index] = {
      ...item,
      selected: checked,
      quantity: checked
        ? item.isEmbryo
          ? item.availableQuantity
          : item.quantity
        : 0,
    };
    setQuantities(newQuantities);
  };

  const handleQuantityChange = (index: number, value: string) => {
    const count = +value.replace(/[^\d]+/, '').replace(/^0+/, '');
    const newQuantities = [...quantities];
    newQuantities[index] = { ...newQuantities[index], quantity: count };
    setQuantities(newQuantities);
  };

  const handleErrors = (index: number) => {
    return quantities[index]?.selected
      ? quantities[index].quantity > quantities[index].availableQuantity
        ? 'error'
        : quantities[index].quantity === 0
        ? 'warning'
        : ''
      : '';
  };

  useEffect(() => {
    const selected = quantities.filter(q => q.selected);
    const valid = selected.length > 0 && selected.every(q => q.quantity > 0 && q.quantity <= q.availableQuantity);
    onChange({ valid, value: selected });
  }, [quantities]);

  return (
    <div className="multi-animal-select-specimens">
      <h3>{LABEL_CONSTANTS.SELECT_ANIMALS}</h3>
      <div className="desk-select-animals">
        <div className="specimen-table">
          <table>
            <thead>
              <tr>
                <th>{TABLE_HEADER_CONSTANTS.SELECT}</th>
                <th>{TABLE_HEADER_CONSTANTS.ANIMAL}</th>
                <th>{TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}</th>
              </tr>
            </thead>
            <tbody>
              {animals.map(({ animal, quantityAvailable }) => (
                <tr key={animal.animalId}>
                  <td>
                    <input
                      type="checkbox"
                      disabled={loadingSpecimens}
                      checked={selectedAnimalIds.includes(animal.animalId!)}
                      onChange={e => toggleAnimal(animal.animalId!, e.target.checked)}
                    />
                  </td>
                  <td>{`${animal.code} - ${animal.name}`}</td>
                  <td>{quantityAvailable}</td>
                </tr>
              ))}
              {loadingAnimal && (
                <tr>
                  <td colSpan={3}>
                    <br />
                    <br />
                    <Loader loaderSize="small" />
                    <br />
                    <br />
                  </td>
                </tr>
              )}
              <tr>
                <td colSpan={3}>&nbsp;</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <>
        <h3>{LABEL_CONSTANTS.SELECT_ITEM_QUANTITY}</h3>
        <div className="desk-select-specimens">
          <div className="specimen-table">
            <table>
              <thead>
                <tr>
                  <th>{TABLE_HEADER_CONSTANTS.SELECT}</th>
                  <th>{TABLE_HEADER_CONSTANTS.ANIMAL}</th>
                  <th>{TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}</th>
                  <th>{TABLE_HEADER_CONSTANTS.LOT_NO}</th>
                  <th>{TABLE_HEADER_CONSTANTS.QUALITY}</th>
                  <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>
                  <th>{TABLE_HEADER_CONSTANTS.FLUSH_MATING}</th>
                  <th>{TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}</th>
                  <th>{TABLE_HEADER_CONSTANTS.QUANTITY}</th>
                </tr>
              </thead>
              <tbody>
                {specimens.map((spec, index) => (
                  <tr
                    className={handleErrors(index)}
                    key={`${spec.specimenId}-${spec.embryoStrawCode || 'no-straw'}-${spec.animal?.animalId}`}>
                    <td>
                      <input
                        type="checkbox"
                        checked={quantities[index]?.selected || false}
                        onChange={e => handleCheckboxChange(index, e.target.checked)}
                      />
                    </td>
                    <td>{`${spec.animal?.code} - ${spec.animal?.name}`}</td>
                    <td>{spec.specimenType?.name}</td>
                    <td>{spec.specimenId}</td>
                    <td>{spec.quality}</td>
                    <td>{spec.embryoStrawCode ?? '-'}</td>
                    <td>{spec.flushMating ?? '-'}</td>
                    <td>{quantities[index]?.availableQuantity}</td>
                    <td>
                      <input
                        type="text"
                        placeholder="0"
                        value={
                          quantities[index]?.selected ? (quantities[index]?.quantity > 0 ? quantities[index]?.quantity : '') : '-'
                        }
                        disabled={!quantities[index]?.selected || quantities[index]?.isEmbryo}
                        onChange={e => !quantities[index]?.isEmbryo && handleQuantityChange(index, e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
                {loadingSpecimens && (
                  <tr>
                    <td colSpan={7}>
                      <>
                        <br />
                        <br />
                        <Loader loaderSize="small" />
                        <br />
                        <br />
                      </>
                    </td>
                  </tr>
                )}
                {selectedAnimalIds.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <>
                        <p>{LABEL_CONSTANTS.NO_ANIMALS_SELECTED}</p>
                      </>
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan={8}>{LABEL_CONSTANTS.TOTAL_QUANTITY}:</td>
                  <td>
                    <input type="text" disabled value={quantities.reduce((acc, q) => (q.selected ? acc + q.quantity : acc), 0)} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        {/** Mobile View */}
        <div className="xs-select-specimens">
          {specimens &&
            specimens?.length > 0 &&
            specimens.map((spec, index) => {
              return (
                <div
                  className={`card max-width ${handleErrors(index)}`}
                  key={`${spec.specimenId}-${spec.embryoStrawCode || 'no-straw'}-${spec.animal?.animalId}`}>
                  <div className="selected-specimens-row">
                    <div className="input-check">
                      {
                        <input
                          type="checkbox"
                          checked={quantities[index]?.selected || false}
                          onChange={e => handleCheckboxChange(index, e.target.checked)}
                        />
                      }
                    </div>
                    <div className="select-specimens">
                      <div className="card-content-section">
                        <div className="left column">
                          <label>{`${TABLE_HEADER_CONSTANTS.ANIMAL}: `}</label>
                          <label>{`${spec.animal?.code} - ${spec.animal?.name}`}</label>
                        </div>
                        <div className="left column">
                          <label>{`${TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}: `}</label>
                          <label>{`${spec.specimenType?.name}`}</label>
                        </div>
                        <div className="right column">
                          <label>{`${TABLE_HEADER_CONSTANTS.LOT_NO}: `}</label>
                          <label>{`${spec.specimenId}`}</label>
                        </div>
                      </div>
                      <div className="card-content-section">
                        <div className="left column">
                          <label>{`${TABLE_HEADER_CONSTANTS.QUALITY}: `}</label>
                          <label>{`${spec.quality}`}</label>
                        </div>
                        <div className="left column">
                          <label>{`${TABLE_HEADER_CONSTANTS.STRAW_LOT}: `}</label>
                          <label>{spec.embryoStrawCode ?? '-'}</label>
                        </div>
                        <div className="left column">
                          <label>{`${TABLE_HEADER_CONSTANTS.FLUSH_MATING}: `}</label>
                          <label>{spec.flushMating ?? '-'}</label>
                        </div>
                        <div className="left column ">
                          <label>{`${TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}: `}</label>
                          <label>{`${quantities[index]?.availableQuantity}`}</label>
                        </div>
                        <div className="right center column">
                          <label>{`${TABLE_HEADER_CONSTANTS.QUANTITY}:`}</label>
                          <input
                            type="text"
                            placeholder="0"
                            value={
                              quantities[index]?.selected
                                ? quantities[index]?.quantity > 0
                                  ? quantities[index]?.quantity
                                  : ''
                                : '-'
                            }
                            disabled={!quantities[index]?.selected || quantities[index]?.isEmbryo}
                            onChange={e => !quantities[index]?.isEmbryo && handleQuantityChange(index, e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          <div className="total-quantity">
            <label>{LABEL_CONSTANTS.TOTAL_QUANTITY}: &nbsp; </label>
            {quantities.reduce(
              (acc: number, qua: SelectedSpecimenQuantity): number => (qua.selected ? acc + qua.quantity : acc),
              0,
            )}
          </div>
        </div>
      </>
    </div>
  );
};

export default MultiAnimalSpecimenSelector;
